import { useEffect } from 'react'
import { IconExternalLink } from '@tabler/icons-react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { Button } from '~/components'
import { Image } from '~/components/Image'
import MaterialsList from '~/components/MaterialsList/MaterialsList'
import ProjectTitle from '~/components/ProjectTitle/ProjectTitle'
import RichText from '~/components/RichText/RichText'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { VideoPlayer } from '~/components/VideoPlayer'

import styles from './ProjectDetails.module.css'
import { ProjectDetailsProps, ProjectResource } from './ProjectDetails.types'

export const ProjectDetails = ({
  id,
  title,
  difficulty,
  category,
  hero,
  summary,
  materials,
  resources,
  width = 'narrow',
  isBookmarked,
}: ProjectDetailsProps) => {
  const difficultyFallback = difficulty ?? 1

  const router = useRouter()
  const {
    query: { to },
  } = router

  useEffect(() => {
    const materialsId = 'materials'

    function scrollToMaterials() {
      const element = document.getElementById(materialsId)
      if (element) {
        const elementTop = element.offsetTop

        window.scrollTo({
          top: elementTop,
          behavior: 'smooth',
        })
      }
    }

    if (to === materialsId) {
      scrollToMaterials()
    }
  }, [to])

  return (
    <SectionContainer
      wrapperClassName={classNames(styles.root)}
      innerClassName={classNames(
        'flex',
        'flex-col',
        'gap-6',
        'pb-8',
        'mx-auto'
      )}
      width={width}>
      <ProjectTitle
        category={category}
        className={classNames('-mb-2')}
        difficulty={difficultyFallback}
        isBookmarked={isBookmarked}
        projectId={id}
        title={title}
      />

      {hero && (
        <div className={classNames(styles.hero, 'pt-4 flex justify-center')}>
          {hero.image ? (
            <Image
              className='rounded-md'
              src={hero.image.src}
              alt={hero.image.altText}
              width={hero.image.width}
              height={hero.image.height}
              loading='eager'
            />
          ) : (
            <VideoPlayer source={hero.video} muted={false} autoplay={false} />
          )}
        </div>
      )}

      {summary && (
        <div className={classNames(styles.detailsSection)}>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyXLargeBold}>
            Summary
          </Typography>
          <RichText content={summary} />
        </div>
      )}

      {materials.length > 0 && (
        <MaterialsList
          id='materials'
          name={title}
          sectionTitle='Materials Required'
          materials={materials}
        />
      )}

      {resources.length > 0 && (
        <div className={classNames(styles.detailsSection, 'mt-2')}>
          <Typography
            tag={TypographyTag.p}
            className='mb-4'
            variant={TypographyVariant.BodyXLargeBold}>
            Resources
          </Typography>
          <div
            className={classNames('flex', 'flex-col', 'items-start', 'gap-4')}>
            {resources.map((resource: ProjectResource, idx: number) => {
              return (
                <Button
                  key={idx}
                  variant='link'
                  href={resource.url}
                  target='_blank'
                  icon={<IconExternalLink color='var(--color-state-success)' />}
                  style={{
                    '--btn-icon-size': '24px',
                    '--btn-link-color': 'var(--greyscale-900)',
                    '--btn-link-color-hover': 'var(--greyscale-900)',
                  }}>
                  {resource.name}
                </Button>
              )
            })}
          </div>
        </div>
      )}
    </SectionContainer>
  )
}
